import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../environments/environment';
import 'rxjs/add/observable/forkJoin';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { AuthService } from './auth.service';
import { switchMap, map } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

const SNAPSHOT_MAX_LONG_SIDE = 1280;
const SNAPSHOT_JPEG_QUALITY = 0.85;

@Injectable()
export class SnapShotService {
  private URL: string = environment.apiUrl;

  snapshots: Array<any> = [];
  snapshotsProTools: Array<any> = [];
  projectImg: any = '';

  constructor(private http: HttpClient, private auth: AuthService) { }

  pushSnapshot(snapshot: any) {
    this.snapshots.push(snapshot);
  }

  pushSnapshotsProTools(snapshot: any) {
    this.snapshotsProTools.push(snapshot);
  }

  setProjectImg(value: any) {
    this.projectImg = value;
  }

  setSnapshots(value: any) {
    this.snapshots = value;
  }

  setSnapshotsProTools(value: any) {
    if (value) {
      this.snapshotsProTools = value;
    }
  }

  getSnapshots() {
    return this.snapshots;
  }

  getSnapshotsProTools(){
    return this.snapshotsProTools;
  }

  deleteSnapShot(path: any): Observable<any> {
    const imgName = path.slice(-14);
    return this.http.delete(`${this.URL}/containers/images/files/${imgName}`,  httpOptions);
  }

  compressImageForSave(imageUrl: string): Observable<string> {
    if (!imageUrl || !this.isDataUrl(imageUrl)) {
      return of(imageUrl);
    }

    return fromPromise(this.compressDataUrl(imageUrl));
  }

  isDataUrl(value: string): boolean {
    return !!value && value.indexOf('data:') === 0;
  }

  compressSnapshotsForSave(snapshots: string[] = []): Observable<string[]> {
    if (!snapshots.length) {
      return of([]);
    }

    return Observable.forkJoin(snapshots.map(snapshot => this.compressImageForSave(snapshot)));
  }

  persistSnapshotsForProject(projectId: string, snapshots: string[] = []): Observable<string[]> {
    if (!snapshots.length) {
      return of([]);
    }

    if (!this.hasInlineSnapshots(snapshots)) {
      return this.compressSnapshotsForSave(snapshots).pipe(
        switchMap(compressed =>
          this.patchProjectSnapshots(projectId, compressed).pipe(
            map(res => (res.snapshots as string[]) || compressed)
          )
        )
      );
    }

    let uploadChain: Observable<string[]> = of([]);

    snapshots.forEach(snapshot => {
      uploadChain = uploadChain.pipe(
        switchMap(currentSnapshots =>
          this.compressImageForSave(snapshot).pipe(
            switchMap(compressedSnapshot => {
              const nextSnapshots = currentSnapshots.concat(compressedSnapshot);
              return this.patchProjectSnapshots(projectId, nextSnapshots).pipe(
                map(res => (res.snapshots as string[]) || nextSnapshots)
              );
            })
          )
        )
      );
    });

    return uploadChain;
  }

  hasInlineSnapshots(snapshots: string[] = []): boolean {
    return snapshots.some(snapshot => this.isDataUrl(snapshot));
  }

  private patchProjectSnapshots(projectId: string, snapshots: string[]): Observable<any> {
    return this.http.patch(
      `${this.URL}/projects/${projectId}?access_token=${this.auth.getToken()}`,
      { snapshots },
      httpOptions
    );
  }

  private compressDataUrl(
    dataUrl: string,
    maxLongSide = SNAPSHOT_MAX_LONG_SIDE,
    quality = SNAPSHOT_JPEG_QUALITY
  ): Promise<string> {
    return new Promise(resolve => {
      let settled = false;
      const finish = (value: string) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(value);
      };

      const timeoutId = setTimeout(() => finish(dataUrl), 8000);
      const img = new Image();
      img.onload = () => {
        clearTimeout(timeoutId);
        let targetWidth = img.naturalWidth;
        let targetHeight = img.naturalHeight;
        const longSide = Math.max(targetWidth, targetHeight);

        if (longSide > maxLongSide) {
          const scale = maxLongSide / longSide;
          targetWidth = Math.round(targetWidth * scale);
          targetHeight = Math.round(targetHeight * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          finish(dataUrl);
          return;
        }

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        finish(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        finish(dataUrl);
      };
      img.src = dataUrl;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/snap-shot.service.ts